import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useLogos from "../../hooks/useLogos";
// Inter, la tipografía del diseño, servida desde el propio dominio en vez de
// Google Fonts: así no se le pide nada a un tercero en cada visita. Se usa la
// versión variable porque el diseño pide pesos intermedios (550, 950) que la
// versión estática no tiene.
import "@fontsource-variable/inter";
import "./landing.css";

// Enlaces externos de la landing. Los que se dejan vacíos simplemente no se
// pintan, igual que hacía la versión estática: así se puede publicar sin
// tener todavía el aviso de privacidad o el WhatsApp de contacto.
const ENLACES = {
  instagram: "https://www.instagram.com/radarfreim/",
  whatsapp: "https://wa.me/525539602776",
  privacidad: "",
  email: "",
};

// El botón flotante de ayuda abre WhatsApp con este mensaje ya escrito.
const MENSAJE_AYUDA =
  "¡Hola, Flavio! Tengo una duda sobre RadarFreim. ¿Me puedes ayudar?";
const ENLACE_AYUDA = ENLACES.whatsapp
  ? `${ENLACES.whatsapp}?text=${encodeURIComponent(MENSAJE_AYUDA)}`
  : "";

// Respaldos de marca: se usan mientras carga la API y si esta falla. Los
// logos "en vivo" salen del panel de Personalizar, así que cambiarlos ahí
// también actualiza la landing sin tocar código.
const MARCA = {
  logo: "/landing/radarfreim-logo-dark.png",
  logoClaro: "/landing/radarfreim-logo-light.png",
  icono: "/landing/radarfreim-symbol.png",
};

// Las mascotas son contenido ilustrado de la landing, no imágenes de marca:
// no se administran desde Personalizar.
const MASCOTAS = {
  flavio: "/landing/flavio.png",
  pina: "/landing/pina.png",
  yoshimi: "/landing/yoshimi.png",
  lala: "/landing/lala.png",
};

// Paquetes de RadarCoins: los mismos precios que cobra la Tienda
// (Backend/src/utils/planesCompra.js). Si cambian, cambian en los tres.
const PAQUETES = [
  { rc: 5, precio: "$12.50 USD", porRC: "$2.50 USD por RC", etiqueta: "IDEAL PARA EMPEZAR" },
  { rc: 10, precio: "$24.00 USD", porRC: "$2.40 USD por RC" },
  { rc: 30, precio: "$69.00 USD", porRC: "$2.30 USD por RC" },
  { rc: 50, precio: "$110.00 USD", porRC: "$2.20 USD por RC" },
  { rc: 100, precio: "$210.00 USD", porRC: "$2.10 USD por RC", etiqueta: "👑 MÁS POPULAR", destacado: true },
  {
    rc: 200,
    precio: "$400.00 USD",
    porRC: "$2.00 USD por RC",
    etiqueta: "★ MEJOR VALOR",
    destacado: true,
    ahorro: "20% DE AHORRO POR RADARCOIN",
  },
];

function Landing() {
  const contenedor = useRef(null);
  const ayuda = useRef(null);
  const logos = useLogos(MARCA);

  // El badge de RadarCoins usa la versión circular del isotipo. Si el admin
  // configuró un ícono propio en Personalizar, ese manda.
  const iconoMoneda =
    logos.icono === MARCA.icono
      ? "/landing/radarfreim-symbol-circle.png"
      : logos.icono;

  useEffect(() => {
    const raiz = contenedor.current;
    if (!raiz) return undefined;

    // El scroll suave se activa solo mientras la landing está montada: es una
    // propiedad de <html>, y dejarla puesta afectaría al resto de la app.
    document.documentElement.classList.add("rf-landing-activa");
    const limpiar = () =>
      document.documentElement.classList.remove("rf-landing-activa");

    const secciones = raiz.querySelectorAll(".observe");
    const prefiereMenosMovimiento = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefiereMenosMovimiento || !("IntersectionObserver" in window)) {
      // Sin animación de entrada: todo se muestra de una vez.
      secciones.forEach((seccion) => seccion.classList.add("in-view"));
      return limpiar;
    }

    // La clase de animación se añade por JS (y no en el markup) para que, si
    // el script no llega a correr, el contenido quede visible en vez de en
    // blanco.
    raiz.classList.add("motion-ready");

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) {
            entrada.target.classList.add("in-view");
            observador.unobserve(entrada.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );

    secciones.forEach((seccion) => observador.observe(seccion));

    return () => {
      observador.disconnect();
      limpiar();
    };
  }, []);

  // El botón de ayuda se presenta solo: a los 6 s late y despliega su texto
  // durante 5 s, y lo repite cada 45 s, como máximo 3 veces. Deja de hacerlo
  // en cuanto la persona lo toca o pasa el cursor encima. En el celular no se
  // despliega si taparía texto o botones de la página: espera a que se libere
  // el espacio, y si se despliega y después queda encima de algo al hacer
  // scroll, se vuelve a cerrar.
  useEffect(() => {
    const raiz = contenedor.current;
    const boton = ayuda.current;
    if (!raiz || !boton) return undefined;

    let temporizadorAparecer;
    let temporizadorLatido;
    let temporizadorCerrar;
    let apariciones = 0;
    let detenido = false;

    const tapaContenidoEnCelular = () => {
      if (window.innerWidth > 640) return false;

      const estilo = window.getComputedStyle(boton);
      const ancho = Math.min(300, window.innerWidth - 32);
      const derecha = window.innerWidth - parseFloat(estilo.right);
      const abajo = window.innerHeight - parseFloat(estilo.bottom);
      const izquierda = derecha - ancho;
      const arriba = abajo - 76;

      return [
        ...raiz.querySelectorAll(
          "h1, h2, h3, p, figcaption, .button, .site-footer a, button, input, textarea, select"
        ),
      ].some((elemento) => {
        const r = elemento.getBoundingClientRect();
        return (
          r.width > 0 &&
          r.height > 0 &&
          r.right > izquierda &&
          r.left < derecha &&
          r.bottom > arriba &&
          r.top < abajo
        );
      });
    };

    const detener = () => {
      detenido = true;
      window.clearTimeout(temporizadorAparecer);
      window.clearTimeout(temporizadorLatido);
      window.clearTimeout(temporizadorCerrar);
      boton.classList.remove("is-pulsing", "is-expanded");
    };

    const aparecer = () => {
      if (detenido || apariciones >= 3) return;
      if (tapaContenidoEnCelular()) {
        temporizadorAparecer = window.setTimeout(aparecer, 3000);
        return;
      }

      apariciones += 1;
      boton.classList.add("is-pulsing");
      temporizadorLatido = window.setTimeout(() => {
        boton.classList.remove("is-pulsing");
        if (detenido) return;
        boton.classList.add("is-expanded");
        temporizadorCerrar = window.setTimeout(
          () => boton.classList.remove("is-expanded"),
          5000
        );
      }, 300);

      if (apariciones < 3)
        temporizadorAparecer = window.setTimeout(aparecer, 45000);
    };

    const prefiereMenosMovimiento = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (!prefiereMenosMovimiento)
      temporizadorAparecer = window.setTimeout(aparecer, 6000);

    const despejarContenido = () => {
      if (boton.classList.contains("is-expanded") && tapaContenidoEnCelular()) {
        window.clearTimeout(temporizadorCerrar);
        boton.classList.remove("is-expanded");
      }
    };

    const eventosQueDetienen = ["pointerenter", "pointerdown", "focus", "click"];
    eventosQueDetienen.forEach((evento) =>
      boton.addEventListener(evento, detener)
    );
    window.addEventListener("scroll", despejarContenido, { passive: true });
    window.addEventListener("resize", despejarContenido);

    // Al salir de la landing (a /login, /register...) no queda nada corriendo.
    return () => {
      detener();
      eventosQueDetienen.forEach((evento) =>
        boton.removeEventListener(evento, detener)
      );
      window.removeEventListener("scroll", despejarContenido);
      window.removeEventListener("resize", despejarContenido);
    };
  }, []);

  return (
    <div className="rf-landing" ref={contenedor}>
      <a className="skip-link" href="#contenido">
        Saltar al contenido
      </a>

      <main id="contenido">
        <div className="top-logo" aria-label="RadarFreim">
          <img src={logos.logoClaro} alt="RadarFreim" />
        </div>

        <section
          id="top"
          className="section hero observe"
          aria-labelledby="hero-title"
        >
          <div className="hero-copy">
            <p className="kicker">01 — HERO</p>
            {/*
              Los espacios explícitos entre los renglones no se ven (cada span
              es un bloque), pero sin ellos el texto del título se leería
              pegado —"NUEVAENCUENTRASU"— para los buscadores y los lectores
              de pantalla, que concatenan el contenido del h1.
            */}
            <h1 id="hero-title">
              <span>LA MÚSICA NUEVA</span>{" "}
              <span className="title-orange">ENCUENTRA</span>{" "}
              <span>SU LUGAR.</span>
            </h1>
            <p className="hero-lede">
              RadarFreim conecta a quien hace música con quien puede hacerla
              llegar más lejos: blogs, playlists, radios, podcasts y creadores
              musicales.
            </p>
            <div className="hero-actions">
              <Link className="button button-primary" to="/register">
                ABRE TU CUENTA <span aria-hidden="true">→</span>
              </Link>
              <Link className="button button-ghost" to="/login">
                YA TENGO CUENTA <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          <div
            className="hero-visual"
            aria-label="RadarFreim conectando artistas y difusores"
          >
            <div className="radar radar-large">
              <span className="radar-sweep" aria-hidden="true"></span>
              <span className="ring ring-1" aria-hidden="true"></span>
              <span className="ring ring-2" aria-hidden="true"></span>
              <span className="ring ring-3" aria-hidden="true"></span>
              <span className="line line-flavio" aria-hidden="true"></span>
              <span className="line line-pina" aria-hidden="true"></span>
              <span className="line line-yoshimi" aria-hidden="true"></span>
              <span className="line line-lala" aria-hidden="true"></span>
              <img
                className="radar-symbol center-node"
                src={logos.icono}
                alt="Isotipo de RadarFreim"
              />
              <figure className="mascot mascot-flavio">
                <img
                  src={MASCOTAS.flavio}
                  alt="Flavio, nutria y CEO de RadarFreim"
                  width="1077"
                  height="1460"
                />
                <figcaption>
                  <strong>FLAVIO</strong>
                  <span>CEO / guía de RadarFreim</span>
                  <em>Conecta los puntos.</em>
                </figcaption>
              </figure>
              <figure className="mascot mascot-pina">
                <img
                  src={MASCOTAS.pina}
                  alt="Pina, música independiente y artista"
                  width="1221"
                  height="1289"
                />
                <figcaption>
                  <strong>PINA</strong>
                  <span>Música independiente / Artista</span>
                  <em>Quiero que más personas escuchen mi música.</em>
                </figcaption>
              </figure>
              <figure className="mascot mascot-yoshimi">
                <img
                  src={MASCOTAS.yoshimi}
                  alt="Yoshimi, editora de blog musical y difusora"
                  width="1024"
                  height="1536"
                />
                <figcaption>
                  <strong>YOSHIMI</strong>
                  <span>Editora de blog musical / Difusora</span>
                  <em>Descubro proyectos y los comparto con mi audiencia.</em>
                </figcaption>
              </figure>
              <figure className="mascot mascot-lala">
                <img
                  src={MASCOTAS.lala}
                  alt="Lala, influencer y creadora musical"
                  width="1199"
                  height="1312"
                />
                <figcaption>
                  <strong>LALA</strong>
                  <span>Influencer / Creadora musical / Difusora</span>
                  <em>Comparto música con mi comunidad.</em>
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section
          className="section intro observe"
          aria-labelledby="que-es-title"
        >
          <div className="section-label">02 — ¿QUÉ ES RADARFREIM?</div>
          <div className="intro-layout">
            <div className="intro-character">
              <figure className="mascot flavio-guide">
                <img
                  src={MASCOTAS.flavio}
                  alt="Flavio explicando qué es RadarFreim"
                  width="1077"
                  height="1460"
                  loading="lazy"
                />
              </figure>
              <p className="dialogue">"Soy Flavio. Bienvenido al Radar."</p>
            </div>
            <div className="intro-copy">
              <h2 id="que-es-title">
                UNA PLATAFORMA PARA QUE LA MÚSICA NUEVA ENCUENTRE A QUIEN PUEDE
                DIFUNDIRLA.
              </h2>
              <p>
                RadarFreim ayuda a artistas independientes a promocionar su
                música y conseguir difusión real, mientras medios, playlists,
                radios, podcasts e influencers descubren proyectos para
                compartir con su audiencia.
              </p>
              <div className="ecosystem" aria-label="Ecosistema RadarFreim">
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M12 3v10"></path>
                    <path d="M8 7h8"></path>
                    <path d="M7 13a5 5 0 0 0 10 0"></path>
                    <path d="M12 18v3"></path>
                  </svg>
                  ARTISTAS
                </span>
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M6 4h12v16H6z"></path>
                    <path d="M9 8h6"></path>
                    <path d="M9 12h6"></path>
                    <path d="M9 16h3"></path>
                  </svg>
                  BLOGS
                </span>
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M6 7h9"></path>
                    <path d="M6 12h9"></path>
                    <path d="M6 17h6"></path>
                    <circle cx="18" cy="17" r="2"></circle>
                  </svg>
                  PLAYLISTS
                </span>
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M5 10h14v8H5z"></path>
                    <path d="M8 10l8-5"></path>
                    <circle cx="9" cy="14" r="1"></circle>
                    <path d="M13 14h3"></path>
                  </svg>
                  RADIOS
                </span>
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M8 12a4 4 0 0 1 8 0v4a4 4 0 0 1-8 0z"></path>
                    <path d="M5 14v2a7 7 0 0 0 14 0v-2"></path>
                    <path d="M12 21v-2"></path>
                  </svg>
                  PODCASTS
                </span>
                <span>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <circle cx="8" cy="8" r="3"></circle>
                    <circle cx="17" cy="16" r="3"></circle>
                    <path d="M10.5 10.5l4 3"></path>
                  </svg>
                  CREADORES
                </span>
              </div>
              <div className="statement-stack">
                <p>MÚSICOS BUSCAN DIFUSIÓN.</p>
                <p>DIFUSORES BUSCAN MÚSICA.</p>
                <p>RADARFREIM CONECTA A LOS DOS.</p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="section how observe"
          aria-labelledby="funciona-title"
        >
          <div className="section-label">03 — ¿Y CÓMO FUNCIONA?</div>
          <div className="section-heading">
            <h2 id="funciona-title">DOS RECORRIDOS. UN MISMO RADAR.</h2>
            <p>
              RadarFreim conecta a los artistas con difusores que pueden
              descubrir, compartir y amplificar su música.
            </p>
          </div>

          <div className="characters-strip" aria-label="Personajes de RadarFreim">
            <article>
              <figure className="mascot">
                <img
                  src={MASCOTAS.pina}
                  alt="Pina representa a artistas independientes"
                  width="1221"
                  height="1289"
                  loading="lazy"
                />
              </figure>
              <div>
                <h3>PINA</h3>
                <p>Música independiente · Artista</p>
                <span>
                  Pina crea música y utiliza RadarFreim para encontrar difusores
                  que ayuden a llevarla a nuevas audiencias.
                </span>
              </div>
            </article>
            <article>
              <figure className="mascot">
                <img
                  src={MASCOTAS.yoshimi}
                  alt="Yoshimi representa a editoras y medios musicales"
                  width="1024"
                  height="1536"
                  loading="lazy"
                />
              </figure>
              <div>
                <h3>YOSHIMI</h3>
                <p>Editora de blog musical · Difusora</p>
                <span>
                  Yoshimi descubre proyectos que le interesan y decide cuáles
                  compartir con su audiencia.
                </span>
              </div>
            </article>
            <article>
              <figure className="mascot">
                <img
                  src={MASCOTAS.lala}
                  alt="Lala representa a creadoras musicales e influencers"
                  width="1199"
                  height="1312"
                  loading="lazy"
                />
              </figure>
              <div>
                <h3>LALA</h3>
                <p>Influencer / creadora musical · Difusora</p>
                <span>
                  Lala comparte música con su comunidad y puede monetizar el
                  trabajo de difusión.
                </span>
              </div>
            </article>
          </div>

          <div className="journeys">
            <article className="journey">
              <h3>ARTISTA</h3>
              <ol className="steps">
                <li>
                  <b>01</b>
                  <strong>ELIGE</strong>
                  <span>Encuentra los difusores que más te interesan.</span>
                </li>
                <li>
                  <b>02</b>
                  <strong>ENVÍA</strong>
                  <span>Utiliza RadarCoins para enviarles tu música.</span>
                </li>
                <li>
                  <b>03</b>
                  <strong>RECIBE FEEDBACK</strong>
                  <span>El difusor escucha y decide aceptar o rechazar.</span>
                </li>
                <li>
                  <b>04</b>
                  <strong>CONECTA</strong>
                  <span>
                    Si acepta, realiza la publicación y te entrega el link.
                  </span>
                </li>
              </ol>
              <div className="result-scene">
                <img
                  src="/landing/pina-gana-audiencia.png"
                  alt="Pina tocando batería frente a una audiencia"
                  width="1536"
                  height="1024"
                  loading="lazy"
                />
                <div>
                  <strong>
                    <span>05</span> GANA AUDIENCIA
                  </strong>
                  <p>
                    ¡Tu música llega a nuevas personas y tu proyecto gana
                    exposición!
                  </p>
                </div>
              </div>
            </article>

            <div className="journey-link" aria-hidden="true">
              <img
                src={iconoMoneda}
                alt=""
                width="1254"
                height="1254"
                loading="lazy"
              />
              <span>RadarCoins</span>
            </div>

            <article className="journey diffuser-journey">
              <h3>DIFUSOR</h3>
              <ol className="steps">
                <li>
                  <b>01</b>
                  <strong>DESCUBRE</strong>
                  <span>Recibe propuestas musicales.</span>
                </li>
                <li>
                  <b>02</b>
                  <strong>ESCUCHA</strong>
                  <span>Conoce la canción y el proyecto.</span>
                </li>
                <li>
                  <b>03</b>
                  <strong>DECIDE</strong>
                  <span>Acepta o rechaza y proporciona feedback.</span>
                </li>
                <li>
                  <b>04</b>
                  <strong>COMPARTE</strong>
                  <span>Si aceptas, publica dentro del plazo establecido.</span>
                </li>
              </ol>
              <div className="result-scene">
                <img
                  src="/landing/difusores-cobran.png"
                  alt="Yoshimi y Lala celebrando el pago recibido"
                  width="1536"
                  height="1024"
                  loading="lazy"
                />
                <div>
                  <strong>
                    <span>05</span> COBRA
                  </strong>
                  <p>
                    ¡Monetiza tu trabajo compartiendo música con tu audiencia!
                  </p>
                </div>
              </div>
            </article>
          </div>

          <div
            className="decision-grid"
            aria-label="Aceptación y rechazo de envíos"
          >
            <article>
              <h3>SI EL DIFUSOR ACEPTA</h3>
              <p>
                Tiene <strong>3 días</strong> para realizar la publicación,
                envía el link al artista y entonces puede cobrar el crédito
                correspondiente.
              </p>
            </article>
            <article>
              <h3>SI EL DIFUSOR RECHAZA</h3>
              <p>
                Debe explicar por qué, no cobra el crédito, el RadarCoin regresa
                a la cuenta del artista y puede utilizarse nuevamente con otro
                difusor.
              </p>
            </article>
          </div>
        </section>

        <section className="section coins observe" aria-labelledby="coins-title">
          <div className="section-label">04 — RADARCOINS</div>
          <div className="coins-grid">
            <div className="coins-intro">
              <h2 id="coins-title">TU MÚSICA EN EL LUGAR CORRECTO.</h2>
              <p>
                Los músicos utilizan RadarCoins para enviar sus canciones a los
                difusores que eligen.
              </p>
              <div className="free-coin">
                <span>1 RADARCOIN GRATIS</span>
                <p>
                  Al crear tu cuenta de músico, utiliza el código{" "}
                  <strong>HOLARADAR</strong> durante el registro y recibe 1
                  RadarCoin gratis para probar la plataforma enviando tu música a
                  un difusor para publicación social.
                </p>
              </div>
              <Link className="button button-primary" to="/register">
                PRUEBA RADARFREIM <span aria-hidden="true">→</span>
              </Link>
            </div>

            <div className="coin-economy">
              <div className="send-types">
                <article>
                  <span>SOCIAL / PLAYLIST SPOTIFY</span>
                  <strong>1 RadarCoin</strong>
                  <p>
                    Para que el difusor comparta la canción en redes sociales o
                    una playlist de Spotify.
                  </p>
                  <b>Difusor recibe: $1 USD</b>
                </article>
                <article>
                  <span>EDITORIAL / ENTREVISTA</span>
                  <strong>5 RadarCoins</strong>
                  <p>
                    Permite solicitar una nota editorial en la página web o una
                    entrevista e incluye amplificación en redes sociales.
                  </p>
                  <b>Medio recibe: $7 USD</b>
                </article>
              </div>

              <div className="packages" aria-label="Paquetes RadarCoins">
                <h3>PAQUETES</h3>
                {/* Los mismos precios que cobra la Tienda
                    (Backend/src/utils/planesCompra.js). */}
                <ul>
                  {PAQUETES.map((paquete) => (
                    <li key={paquete.rc}>
                      <span className="package-name">
                        {paquete.rc} RadarCoins
                        {paquete.etiqueta && (
                          <>
                            {" "}
                            <span
                              className={
                                paquete.destacado
                                  ? "package-tag package-tag-featured"
                                  : "package-tag"
                              }
                            >
                              {paquete.etiqueta}
                            </span>
                          </>
                        )}
                      </span>
                      <span className="package-price">
                        <strong>{paquete.precio}</strong>
                        <span>{paquete.porRC}</span>
                        {paquete.ahorro && (
                          <span className="package-saving">{paquete.ahorro}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <p>
                  Mientras más RadarCoins compras, menor es el costo por
                  crédito.
                </p>
              </div>

              <div className="coin-notes">
                <article>
                  <h3>COBRO DE DIFUSORES</h3>
                  <p>
                    Los difusores pueden solicitar su pago desde la plataforma.
                    Mínimo para solicitar cobro: <strong>$30 USD</strong>. El
                    pago se realiza manualmente mediante PayPal.
                  </p>
                </article>
                <article>
                  <h3>ACEPTACIÓN</h3>
                  <p>
                    RadarFreim garantiza aproximadamente un{" "}
                    <strong>80% de aceptación</strong> en los envíos.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="section match observe" aria-labelledby="match-title">
          <div className="match-mascots" aria-label="Personajes de RadarFreim">
            <figure className="mascot match-flavio">
              <img
                src={MASCOTAS.flavio}
                alt="Flavio, CEO de RadarFreim"
                width="1077"
                height="1460"
                loading="lazy"
              />
            </figure>
            <figure className="mascot match-pina">
              <img
                src={MASCOTAS.pina}
                alt="Pina, música independiente y artista"
                width="1221"
                height="1289"
                loading="lazy"
              />
            </figure>
            <figure className="mascot match-yoshimi">
              <img
                src={MASCOTAS.yoshimi}
                alt="Yoshimi, editora de blog musical y difusora"
                width="1024"
                height="1536"
                loading="lazy"
              />
            </figure>
            <figure className="mascot match-lala">
              <img
                src={MASCOTAS.lala}
                alt="Lala, influencer y creadora musical"
                width="1199"
                height="1312"
                loading="lazy"
              />
            </figure>
          </div>
          <h2 id="match-title">Encuentra tu match.</h2>
          <p>El proyecto musical encuentra difusión.</p>
          <p>El difusor encuentra música y una oportunidad de monetización.</p>
          <strong className="match-statement">
            La conexión correcta puede llevar una canción mucho más lejos.
          </strong>
        </section>

        <section className="final-band observe" aria-labelledby="final-title">
          <div className="section final-cta">
            <div className="section-label">06 · ÚNETE AL RADAR</div>
            <div className="final-grid">
              <h2 id="final-title">
                ¿Listo para entrar al <span>Radar</span>?
              </h2>
              <div
                className="final-points"
                aria-label="Beneficios para unirse al Radar"
              >
                <p>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <circle cx="10" cy="10" r="6"></circle>
                    <path d="M15 15l5 5"></path>
                  </svg>
                  Descubre música nueva.
                </p>
                <p>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M9 7l-2 2a4 4 0 0 0 0 6"></path>
                    <path d="M15 17l2-2a4 4 0 0 0 0-6"></path>
                    <path d="M8 12h8"></path>
                  </svg>
                  Conecta proyectos.
                </p>
                <p>
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M5 19V9"></path>
                    <path d="M12 19V5"></path>
                    <path d="M19 19v-7"></path>
                  </svg>
                  Haz que tu trabajo también cuente.
                </p>
              </div>
              <div className="final-actions">
                <Link className="button button-primary" to="/register">
                  ABRE TU CUENTA <span aria-hidden="true">→</span>
                </Link>
                <Link className="button button-dark" to="/login">
                  YA TENGO CUENTA
                </Link>
              </div>
            </div>
          </div>

          <footer className="site-footer">
            <img
              className="footer-mark"
              src={logos.logo}
              alt="RadarFreim"
              loading="lazy"
            />
            <nav aria-label="Contacto y redes de RadarFreim">
              {ENLACES.instagram && (
                <a
                  className="icon-link"
                  href={ENLACES.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram oficial de RadarFreim"
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="5"></rect>
                    <circle cx="12" cy="12" r="4"></circle>
                    <circle cx="17.5" cy="6.5" r="1"></circle>
                  </svg>
                </a>
              )}
              {ENLACES.whatsapp && (
                <a
                  className="icon-link"
                  href={ENLACES.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp de RadarFreim"
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M5 19l1.1-3.6a7.5 7.5 0 1 1 2.7 2.6L5 19z"></path>
                    <path d="M9.2 8.8c.2 3 2.2 5.1 5.2 5.9l1.3-1.3"></path>
                  </svg>
                </a>
              )}
              {ENLACES.email && <a href={"mailto:" + ENLACES.email}>Email</a>}
              {ENLACES.privacidad && (
                <a
                  href={ENLACES.privacidad}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Aviso de privacidad
                </a>
              )}
            </nav>
            <span>© RadarFreim {new Date().getFullYear()}</span>
          </footer>
        </section>
      </main>

      {ENLACE_AYUDA && (
        <a
          ref={ayuda}
          className="whatsapp-help"
          href={ENLACE_AYUDA}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Ayuda por WhatsApp"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.76-1.653-2.057-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.496.1-.198.05-.372-.025-.52-.074-.149-.669-1.612-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.875 1.213 3.073c.149.198 2.096 3.2 5.077 4.487.709.306 1.261.489 1.691.625.71.226 1.356.194 1.866.118.569-.085 1.758-.719 2.005-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.36-.214-3.74.98.998-3.646-.234-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.435-9.884 9.889-9.884a9.82 9.82 0 0 1 6.99 2.896 9.82 9.82 0 0 1 2.894 6.991c-.002 5.45-4.436 9.885-9.892 9.885m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.158 11.892a11.85 11.85 0 0 0 1.587 5.936L.057 24l6.318-1.657a11.88 11.88 0 0 0 5.674 1.447h.005c6.557 0 11.892-5.335 11.894-11.892a11.82 11.82 0 0 0-3.484-8.414z" />
          </svg>
          <span className="whatsapp-help-copy" aria-hidden="true">
            <strong>¿Tienes dudas? ¡Te ayudamos!</strong>
            <span>Escríbenos por WhatsApp.</span>
          </span>
        </a>
      )}
    </div>
  );
}

export default Landing;
